/**
 * GDJ favourites page — hydrate localStorage favourites into product cards via
 * the self-hosted search engine (POST /api/v1/products/by-ids). Replaces the
 * former Algolia getObjects call. Re-renders when favourites change.
 */
(function () {
  function init() {
    const root = document.querySelector('.gdj-favpage');
    if (!root) return;
    const grid = root.querySelector('[data-fav-grid]');
    const empty = root.querySelector('[data-fav-empty]');
    const summary = root.querySelector('[data-fav-summary]');
    const cfg = root.dataset;
    const API = (cfg.searchApiUrl || '').replace(/\/$/, '');
    const SHOP = cfg.shop;
    let labels = {};
    try { labels = JSON.parse(cfg.labels || '{}'); } catch (_) {}

    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const MONEY = cfg.moneyFormat || '{{amount}} €';
    const money = (v) => {
      const n = Number(v); if (!isFinite(n)) return '';
      const a = n.toFixed(2), nd = Math.round(n).toString();
      return MONEY.replace(/\{\{\s*amount\s*\}\}/g, a)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, nd)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, a.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, nd);
    };
    const numId = (gid) => String(gid || '').split('/').pop();
    const heart = '<svg class="gdj-icon gdj-icon--heart" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
    const cartSvg = '<svg class="gdj-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';
    const starSvg = '<svg class="gdj-star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.756a2.122 2.122 0 0 0 1.597-1.16z"/></svg>';
    const starsHtml = (rating, count) => {
      const pct = Math.max(0, Math.min(5, Number(rating) || 0)) * 20;
      const row = starSvg.repeat(5);
      return '<span class="gdj-card__rating"><span class="gdj-stars" style="--gdj-stars: ' + pct + '%">' +
        '<span class="gdj-stars__off">' + row + '</span><span class="gdj-stars__on" aria-hidden="true">' + row + '</span></span>' +
        (count ? '<span class="gdj-card__rating-count">(' + count + ')</span>' : '') + '</span>';
    };

    async function load() {
      const favs = window.gdjFavorites ? gdjFavorites.getAll() : [];
      if (!favs.length || !API || !SHOP) { render([]); return; }
      try {
        const res = await fetch(`${API}/api/v1/products/by-ids`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop: SHOP, ids: favs.map((f) => String(f.id)) }),
        });
        if (!res.ok) { render([]); return; }
        const data = await res.json();
        render(data.products || []);
      } catch (_) {
        render([]);
      }
    }

    function render(products) {
      if (!products.length) { grid.hidden = true; empty.hidden = false; summary.textContent = ''; return; }
      empty.hidden = true; grid.hidden = false;
      summary.textContent = `${products.length} ${products.length === 1 ? (labels.item || 'article') : (labels.items || 'articles')}`;
      const showRatings = cfg.showRatings !== 'false';
      grid.innerHTML = products.map((p) => {
        const img = (p.images && p.images[0] && p.images[0].url) || (p.variants && p.variants[0] && p.variants[0].image) || '';
        const onSale = p.special_price != null && p.regular_price != null && Number(p.special_price) < Number(p.regular_price);
        const url = `${rootUrl}products/${esc(p.handle)}`;
        const id = esc(numId(p.id));
        const brand = p.brand || '';
        const sku = (p.variants && p.variants[0] && p.variants[0].sku) ? String(p.variants[0].sku).trim() : '';
        let ratingHtml = '';
        if (showRatings && p.rating) ratingHtml = starsHtml(p.rating, p.rating_count);
        const priceVal = p.special_price != null ? p.special_price : p.regular_price;
        return `<li class="gdj-card" data-product-id="${id}" data-product-handle="${esc(p.handle)}">
          <div class="gdj-card__media-wrap">
            <div class="gdj-card__corner">
              ${p.is_preorder ? `<span class="gdj-card__badge gdj-card__badge--preorder">${esc(labels.preorder || 'Précommande')}</span>` : ''}
              ${onSale ? `<span class="gdj-card__badge">${esc(labels.sale || 'Promo')}</span>` : ''}
              <span class="gdj-card__pill gdj-card__pill--seen" data-gdj-seenpill hidden>${esc(labels.seen || 'Vu')}</span>
              <span class="gdj-card__pill gdj-card__pill--fav" data-gdj-favpill>${heart}${esc(labels.in_favorites || '')}</span>
            </div>
            <span class="gdj-card__incart" data-gdj-incart hidden><span class="gdj-card__incart-x">&times;</span><b data-gdj-incart-qty>1</b></span>
            <a href="${url}" class="gdj-card__media" aria-label="${esc(p.title)}">${img ? `<img class="gdj-card__img" src="${esc(img)}" alt="" loading="lazy">` : ''}</a>
            <button type="button" class="gdj-card__fav gdj-fav--active" data-fav-btn="${id}" data-fav-handle="${esc(p.handle)}" aria-label="${esc(labels.wishlist || '')}">${heart}</button>
          </div>
          <div class="gdj-card__body">
            ${ratingHtml}
            <a href="${url}" class="gdj-card__title">${esc(p.title)}</a>
            ${sku ? `<div class="gdj-card__sku">${esc(labels.sku || 'Réf.')} ${esc(sku)}</div>` : ''}
            <div class="gdj-card__price">${onSale
              ? `<span class="gdj-card__price-sale">${money(p.special_price)}</span> <s class="gdj-card__price-was">${money(p.regular_price)}</s>`
              : `<span>${money(priceVal)}</span>`}</div>
            <button type="button" class="gdj-card__quick" data-gdj-quick-add="${esc(p.handle)}">${cartSvg}<span>${esc(p.is_preorder ? (labels.preorder || 'Précommande') : (labels.quick_add || 'Ajouter'))}</span></button>
          </div>
        </li>`;
      }).join('');
      document.dispatchEvent(new CustomEvent('gdj:cards-added'));
    }

    document.addEventListener('gdj:favorites-change', load);
    if (window.gdjFavorites) { load(); } else { setTimeout(load, 300); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
